/**
 * Who was on the call, as one line.
 *
 * Extracted from the record panel because the same small rule went wrong there
 * twice. A raw telephony feed names almost nobody: Stream Telecom sends
 * `manager` = '0' on every row and volunteers no contact name, so both sides of
 * a call frequently arrive as bare numbers. The rule is therefore symmetric —
 * each side falls back to its number — and it was not: the agent fell back, the
 * counterparty did not, so an inbound call rendered our own line and dropped
 * the customer's number, the one identifying fact such a record has. The record
 * list beside the panel showed it correctly the whole time.
 *
 * The panel additionally prints the line the call went through. That is why
 * `agent_phone` deliberately holds nothing on an inbound call whose leg IS the
 * line (see `agentLineOf` in `@aica/connectors`) — otherwise one number appears
 * in two fields and neither of them is the customer.
 */

export interface ParticipantsSource {
  agentName?: string | null;
  agentPhone?: string | null;
  participantName?: string | null;
  participantPhone?: string | null;
}

/** A name, or the number when there is no name, or nothing when neither. */
function plainSide(name?: string | null, phone?: string | null): string | null {
  return name || phone || null;
}

/** A name with its number in parentheses; the bare number when unnamed. */
function detailedSide(name?: string | null, phone?: string | null): string | null {
  if (!name) return phone || null;
  return phone ? `${name} (${phone})` : name;
}

function join(agent: string | null, participant: string | null, arrow: string) {
  return agent && participant ? `${agent} ${arrow} ${participant}` : agent || participant;
}

/**
 * Compact form for places that carry the label somewhere else — a breadcrumb, a
 * created task's title — where a number in parentheses would only add noise.
 *
 * `arrow` encodes the direction (outbound `→`, inbound `←`); the agent stays
 * first either way so the visual order does not jump between records.
 */
export function participantsPlainLabel(
  record: ParticipantsSource | null | undefined,
  arrow: string
): string | null {
  if (!record) return null;
  return join(
    plainSide(record.agentName, record.agentPhone),
    plainSide(record.participantName, record.participantPhone),
    arrow
  );
}

/** Full form for the panel header: names with their numbers. */
export function participantsDetailedLabel(
  record: ParticipantsSource | null | undefined,
  arrow: string
): string | null {
  if (!record) return null;
  return join(
    detailedSide(record.agentName, record.agentPhone),
    detailedSide(record.participantName, record.participantPhone),
    arrow
  );
}
